import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useNavigate, useParams } from "react-router-dom";
import DeliveryPincodesDialog from "./DeliveryPincodesDialog";
import { EmptyAddCard } from "../../components/settings/EmptyAddCard";
import { getRacksByRoomId, deleteRack } from "../../services/WarehouseSettings";
import AddEditRack from "../../components/settings/AddEditRack";

const RacksPage = () => {
  const navigate = useNavigate();
  const { buildingId, floorId, roomId } = useParams();
  const [racks, setRacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRack, setSelectedRack] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [pincodesDialogOpen, setPincodesDialogOpen] = useState(false);

  const loadRacks = useCallback(async () => {
    try {
      if (!roomId) return;
      setLoading(true);
      const data = await getRacksByRoomId(Number(roomId));
      setRacks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load failed:", err);
      setRacks([]);
      setSnackbar({ open: true, message: "Failed to load racks", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  const saveDeliveryPincodes = async (pincodes: string[]) => {
    try {
      console.log("Saved pincodes:", pincodes);
      setSnackbar({ open: true, message: "Delivery pincodes saved successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to save delivery pincodes", severity: "error" });
    }
  };

  useEffect(() => {
    if (roomId) {
      loadRacks();
    }
  }, [roomId, loadRacks]);

  const openAddRack = () => {
    setEditData(null);
    setDialogOpen(true);
  };

  const openEditRack = (rack: any) => {
    setEditData(rack);
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    loadRacks();
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRack(id);
      loadRacks();
      setSnackbar({ open: true, message: "Rack deleted successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete rack", severity: "error" });
    }
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          onClick={() => navigate(`/settings/warehouse/buildings/${buildingId}/floors/${floorId}/rooms`)}
          sx={{ border: "none", textTransform: "none", color: "text.primary" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => setPincodesDialogOpen(true)}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "transparent",
              borderColor: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
          endIcon={<SettingsOutlinedIcon sx={{ color: "primary.main" }} />}
        >
          Delivery Pin codes
        </Button>
      </Box>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">All Racks - {racks.length}</Typography>
          {racks.length > 0 && (
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={openAddRack}
              sx={{ borderRadius: "6px", textTransform: "none" }}
            >
              Add Rack
            </Button>
          )}
        </Box>

        <Box sx={{ 
          flex: 1, 
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px"
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#a8a8a8"
            }
          },
          "&::-webkit-scrollbar-button": {
            display: "none"
          }
        }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
              <CircularProgress />
            </Box>
          ) : racks.length === 0 ? (
            <EmptyAddCard
              onAdd={openAddRack}
              title="Add Rack"
              description="Click here to add your first rack"
            />
          ) : (
            <Grid container spacing={2}>
              {racks.map((rack) => (
              <Grid item xs={12} key={rack.id}>
                <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "grey.300" }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography fontWeight={600}>{rack.rackName}</Typography>
                      </Box>
                      <IconButton
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedRack(rack);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        maxHeight: "200px",
                        overflow: "auto",
                        mb: 2,
                        "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(50px, 1fr))",
                          gap: 1,
                        }}
                      >
                        {Array.from({ length: (rack.rows || 1) * (rack.coloumns || 1) }).map((_, index) => {
                          const r = Math.floor(index / (rack.coloumns || 1)) + 1;
                          const c = (index % (rack.coloumns || 1)) + 1;
                          
                          // Find bin data for this position
                          const binData = rack.bins?.find((bin: any) => 
                            Number(bin.row) === r && Number(bin.coloumn) === c
                          );
                          
                          // Determine color based on availability percentage
                          let backgroundColor = "grey.50"; // Default for empty positions
                          let textColor = "text.primary";
                          let displayText = "NA";
                          
                          if (binData) {
                            backgroundColor = binData.availablePercentage === 0.0 && binData.usedPercentage >= 100 ? "error.main" : binData.availablePercentage <= 30 ? "warning.main" : "success.main";
                           displayText = `R ${(r - 1) * (rack.coloumns || 1) + c}`;
                          }
                          
                          return (
                            <Box
                              key={index}
                              sx={{
                                height: 50,
                                borderRadius: 1,
                                backgroundColor,
                                color: textColor,
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 500,
                              }}
                            >
                              {displayText}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="body2">Capacity</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={rack.availablePercentage === 0.0 && rack.usedPercentage >= 100 ? rack.usedPercentage : rack.availablePercentage}
                        sx={{
                          height: 6,
                          borderRadius: 4,
                          backgroundColor: "#e5e7eb",
                          "& .MuiLinearProgress-bar": { 
                            backgroundColor: rack.availablePercentage === 0.0 && rack.usedPercentage >= 100 ? "error.main" : rack.availablePercentage <= 30 ? "warning.main" : "success.main"
                          },
                        }}
                      />
                      <Typography variant="caption">
                        { rack.availablePercentage === 0.0 && rack.usedPercentage >= 100 ? rack.usedPercentage : rack.availablePercentage}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => openEditRack(selectedRack)} sx={{ color: "#005EFF" }}>
          <Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem", mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() =>
            navigate(
              `/settings/warehouse/buildings/${buildingId}/floors/${floorId}/rooms/${roomId}/racks/${selectedRack?.id}/bins`
            )
          }
        >
          <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
          View
        </MenuItem>
        {selectedRack?.canDelete && (
          <MenuItem sx={{ color: "red" }} onClick={() => handleDelete(selectedRack?.id)}>
            <Box component="img" src="/delete.svg" alt="Delete" sx={{ width: "1.2rem", height: "1.2rem", mr: 0.5 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      <AddEditRack
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editData={editData}
        buildingId={buildingId!}
        floorId={floorId!}
        roomId={roomId!}
        onSuccess={handleSuccess}
      />

      <DeliveryPincodesDialog
        open={pincodesDialogOpen}
        onClose={() => setPincodesDialogOpen(false)}
        onSave={saveDeliveryPincodes}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default RacksPage;
